import { Badge } from '@fluentui/react-components';
import type { Quote } from '../api/quotes.js';

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
}

/**
 * Estado del presupuesto RESPECTO A OPUS 27 (su destino real). El avance interno
 * en m0 (armar la hoja / buscar equivalentes / precios) ya lo muestran el stepper
 * y los KPIs, así que este pill NO lo repite — solo dice dónde está el presupuesto
 * frente a OPUS 27, y se oculta cuando no hay nada que rastrear todavía.
 *
 * Estados: "Listo para OPUS 27" (armado y costeado, aún no importado) ·
 * "En OPUS 27 · <obra> · <fecha>" (ya importado, final) · "Descargado…"
 * (compat: descarga manual de archivo; el flujo normal es que OPUS 27 lo importe
 * por API). Oculto si no está listo (sin hoja, armando, borrador sin precios, fallido).
 */
export function OpusTrackingChip({
  quote,
}: {
  quote: Pick<Quote, 'opusImportedAt' | 'opusDownloadedAt' | 'opusObraName' | 'status'>;
}) {
  // Ya está en OPUS 27 (estado final).
  if (quote.opusImportedAt) {
    return (
      <Badge appearance="filled" size="medium" color="success">
        En OPUS 27 · &ldquo;{quote.opusObraName}&rdquo; · {fmtDate(quote.opusImportedAt)}
      </Badge>
    );
  }
  // Compat: descarga manual del archivo (hoy OPUS 27 lo importa por API; se
  // conserva por si algún presupuesto viejo tiene esta marca).
  if (quote.opusDownloadedAt) {
    return (
      <Badge appearance="tint" size="medium" color="informative" title="Se descargó el archivo; OPUS 27 aún no lo importó como obra.">
        Descargado · pendiente de importar en OPUS 27 · {fmtDate(quote.opusDownloadedAt)}
      </Badge>
    );
  }
  // Solo se muestra "listo" cuando el presupuesto terminó de armarse y costearse.
  // En cualquier otro estado no hay nada que rastrear frente a OPUS 27 → sin pill.
  const listo = quote.status === 'done' || quote.status === 'completed';
  if (!listo) return null;
  return (
    <Badge
      appearance="outline"
      size="medium"
      style={{ color: 'var(--text-secondary)', borderColor: 'var(--border-default)' }}
      title="El presupuesto está listo; cuando lo importes desde OPUS 27 aquí figurará como 'En OPUS 27'."
    >
      Listo para OPUS 27
    </Badge>
  );
}
